import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import '@/styles/access.css';
import '@/styles/arrival.css';

// TODO: Prep for future entry sound
// const entrySound = new Audio('/sounds/entry.mp3');
// entrySound.volume = 0.15;

const AppPreviewPage = () => {
  const navigate = useNavigate();
  const bgRef = useRef(null);
  const [returning] = useState(() => {
    try { return localStorage.getItem('nymphe_entered') === 'true'; } catch { return false; }
  });
  const [exiting, setExiting] = useState(false);
  const [activeProfile, setActiveProfile] = useState(null);
  const [chatProfile, setChatProfile] = useState(null);
  const [gateProfile, setGateProfile] = useState(null);
  const [gateStep, setGateStep] = useState(0);
  const [presence, setPresence] = useState(() => {
    try { return localStorage.getItem('nymphe_presence') === 'true'; } catch { return false; }
  });
  const [showFelt, setShowFelt] = useState(false);
  const [kissed, setKissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem('nymphe_kissed_profiles') || '[]'); } catch { return []; }
  });
  const [kissFeedback, setKissFeedback] = useState(null);
  const [echoProfile, setEchoProfile] = useState(null);
  const [echoText, setEchoText] = useState(null);
  const [ambientLine] = useState(() => {
    const lines = ['Others are here.', "You're not alone here.", 'Presence surrounds you.'];
    return lines[Math.floor(Math.random() * lines.length)];
  });

  const PROFILES = [
    { name: 'Elara', age: 24, img: '/images/tile-elara.jpg', status: '1 km away', activity: 'Active recently', verified: true },
    { name: 'Elias', age: 26, img: '/images/tile-elias.jpg', status: '0 m away', activity: 'Active recently', verified: true },
    { name: 'Sofia', age: 27, img: '/images/tile-sofia.jpg', status: '3 km away', activity: 'Active now', verified: true },
    { name: 'Marcus', age: 29, img: '/images/tile-marcus.jpg', status: '2 km away', activity: 'Active recently', verified: false },
    { name: 'Luna', age: 23, img: '/images/tile-luna.jpg', status: '0 m away', activity: 'Active now', verified: true },
    { name: 'Alex', age: 31, img: '/images/tile-alex.jpg', status: '5 km away', activity: 'Active recently', verified: true },
  ];

  // Session flag — only confirm if already gated
  useEffect(() => {
    if (localStorage.getItem('nymphe_access_token')) {
      localStorage.setItem('nymphe_entered', 'true');
    }
  }, []);

  // Ambient parallax
  useEffect(() => {
    let raf;
    const handleMove = (e) => {
      if (!bgRef.current) return;
      const x = (e.clientX / window.innerWidth - 0.5) * 6;
      const y = (e.clientY / window.innerHeight - 0.5) * 4;
      raf = requestAnimationFrame(() => {
        bgRef.current.style.transform = `translate(${x}px, ${y}px) scale(1.04)`;
      });
    };
    window.addEventListener('mousemove', handleMove);
    return () => { window.removeEventListener('mousemove', handleMove); cancelAnimationFrame(raf); };
  }, []);

  // Delayed kiss feedback — ambiguous echo from one kissed profile
  useEffect(() => {
    const alreadyTriggered = sessionStorage.getItem('nymphe_kiss_echo');
    if (alreadyTriggered || kissed.length === 0) return;
    // ~30% chance of not triggering at all
    if (Math.random() < 0.3) return;
    const delay = 8000 + Math.random() * 12000;
    const pick = kissed[Math.floor(Math.random() * kissed.length)];
    const texts = ['Seen.', '\u2026felt.'];
    const text = texts[Math.floor(Math.random() * texts.length)];
    const timer = setTimeout(() => {
      setEchoProfile(pick);
      setEchoText(text);
      sessionStorage.setItem('nymphe_kiss_echo', 'true');
      setTimeout(() => setEchoText(null), 1200);
    }, delay);
    return () => clearTimeout(timer);
  }, [kissed]);

  // Timing — first visit is ceremonial, return is direct
  const d = returning
    ? { bg: 0, h1: 0.1, sub1: 0.2, sub2: 0.3, btn: 0.4, micro: 0.5, presence: 0.6, ambient: 1.0 }
    : { bg: 0, h1: 0.4, sub1: 0.7, sub2: 0.95, personal: 1.15, btn: 1.4, micro: 1.7, presence: 2.0, ambient: 2.4 };

  const handlePresence = () => {
    if (presence) return;
    setPresence(true);
    try { localStorage.setItem('nymphe_presence', 'true'); } catch {}
    setTimeout(() => setShowFelt(true), 600);
  };

  const handleKiss = (name) => {
    if (kissed.includes(name)) return;
    const next = [...kissed, name];
    setKissed(next);
    setKissFeedback(name);
    try { localStorage.setItem('nymphe_kissed_profiles', JSON.stringify(next)); } catch {}
    setTimeout(() => setKissFeedback(null), 1200);
  };

  const handleMessage = (profile) => {
    if (kissed.includes(profile.name)) {
      // Kissed — open chat, with occasional subtle delay
      if (Math.random() < 0.3) {
        setGateProfile(profile);
        setGateStep(3); // brief pause state
        setTimeout(() => {
          setGateProfile(null);
          setGateStep(0);
          setActiveProfile(null);
          setChatProfile(profile);
        }, 2500);
      } else {
        setActiveProfile(null);
        setChatProfile(profile);
      }
    } else {
      // Not kissed — show gate
      setGateProfile(profile);
      setGateStep(1);
      setTimeout(() => setGateStep(2), 1000);
    }
  };

  const handleGateKiss = () => {
    if (!gateProfile) return;
    handleKiss(gateProfile.name);
    setGateStep(3);
    setTimeout(() => {
      setGateProfile(null);
      setGateStep(0);
      setActiveProfile(null);
      setChatProfile(gateProfile);
    }, 1500);
  };

  const handleReturn = () => {
    setExiting(true);
    setTimeout(() => navigate('/access'), 600);
  };

  return (
    <div className="acc arrival" data-testid="app-preview-page">
      <div className="acc-bg" ref={bgRef}>
        <img src="/images/access-bg.png" alt="" />
        <div className="acc-bg-overlay" />
      </div>
      <div className="arrival-grain" />

      <AnimatePresence>
        {!exiting && (
          <motion.section
            className="acc-step"
            data-testid="arrival-state"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: exiting ? 0.6 : (returning ? 0.4 : 0.8) }}
          >
        <div className="acc-content">
          <motion.h1
            className="acc-headline"
            initial={{ opacity: 0, y: returning ? 10 : 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: d.h1, duration: returning ? 0.6 : 1, ease: [0.22, 1, 0.36, 1] }}
          >
            Welcome inside.
          </motion.h1>
          <motion.p
            className="acc-sub"
            initial={{ opacity: 0, y: returning ? 6 : 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: d.sub1, duration: returning ? 0.5 : 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            Your access has been confirmed.
          </motion.p>
          <motion.p
            className="acc-sub"
            initial={{ opacity: 0, y: returning ? 4 : 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: d.sub2, duration: returning ? 0.5 : 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            {returning ? 'Welcome back.' : "You're inside. The next layer opens soon."}
          </motion.p>

          {!returning && (
            <motion.p
              className="arrival-personal"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: d.personal, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              Tailored to your presence.
            </motion.p>
          )}

          <motion.div
            className="arrival-signal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: d.presence, duration: 0.7 }}
          >
            <span className="arrival-signal-label">Signal your presence</span>
            <button
              className={`arrival-signal-btn ${presence ? 'arrival-signal-btn--done' : ''}`}
              onClick={handlePresence}
              disabled={presence}
              data-testid="presence-btn"
            >
              {presence ? 'Presence noted.' : "I'm here"}
            </button>
            {showFelt && (
              <motion.span
                className="arrival-signal-felt"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                You'll be felt.
              </motion.span>
            )}
          </motion.div>

          <motion.button
            className="acc-btn-return arrival-btn"
            onClick={handleReturn}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: d.btn, duration: 0.6 }}
            data-testid="arrival-return-btn"
            style={returning ? {} : { marginTop: 0 }}
          >
            Return to access
          </motion.button>
          <motion.p
            className="acc-success-micro"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: d.micro, duration: 0.5 }}
            style={{ marginTop: 32 }}
          >
            This space is private.
          </motion.p>
          <motion.p
            className="arrival-ambient"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.15 }}
            transition={{ delay: d.ambient, duration: 0.8 }}
          >
            {ambientLine}
          </motion.p>

          <motion.div
            className="arrival-discovery"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: d.ambient + 0.4, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="arrival-discovery-label">Inside now</span>
            <div className="arrival-discovery-grid">
              {PROFILES.map(p => (
                <div
                  key={p.name}
                  className={`arrival-tile ${kissed.includes(p.name) ? 'arrival-tile--kissed' : ''} ${echoProfile === p.name ? 'arrival-tile--echo' : ''}`}
                  onClick={() => setActiveProfile(p)}
                >
                  <div className="arrival-tile-img-wrap">
                    <img src={p.img} alt="" className="arrival-tile-img" loading="lazy" />
                    <span className="arrival-tile-dot" />
                    <span className="arrival-tile-kiss-hint" />
                  </div>
                  <span className="arrival-tile-name">{p.name}</span>
                  {kissFeedback === p.name && (
                    <motion.span
                      className="arrival-tile-feedback"
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      Kiss sent.
                    </motion.span>
                  )}
                  {echoProfile === p.name && echoText && (
                    <motion.span
                      className="arrival-tile-feedback arrival-tile-echo-text"
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4 }}
                    >
                      {echoText}
                    </motion.span>
                  )}
                </div>
              ))}
            </div>
          </motion.div>

          {/* ── Messaging preview ── */}
          <motion.div
            className="arrival-chat"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: d.ambient + 0.8, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="arrival-chat-header">
              <span className="arrival-chat-name">Elias</span>
              <span className="arrival-chat-status">Active recently</span>
            </div>
            <div className="arrival-chat-thread">
              <div className="arrival-chat-map">
                <img src="/images/map-melbourne-web.mp4" alt="" style={{ display: 'none' }} />
                <div className="arrival-chat-map-inner">
                  <div className="arrival-chat-map-pin" />
                  <span className="arrival-chat-map-label">Approximate location</span>
                </div>
              </div>
              <div className="achat-msg achat-msg--them">
                <span>Are you close?</span>
                <span className="achat-time">8:41 PM</span>
              </div>
              <div className="achat-msg achat-msg--you">
                <span>Just leaving now</span>
                <span className="achat-time">8:42 PM</span>
              </div>
              <div className="achat-msg achat-msg--them">
                <span>Text me when you're here</span>
                <span className="achat-time">now</span>
              </div>
            </div>
            <div className="arrival-chat-input">
              <span className="arrival-chat-placeholder">Say something...</span>
              <svg className="arrival-chat-send" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M22 2L11 13"/><path d="M22 2L15 22 11 13 2 9z"/></svg>
            </div>
          </motion.div>
        </div>
      </motion.section>
      )}
      </AnimatePresence>

      {/* ── Profile Reveal Overlay ── */}
      <AnimatePresence>
        {activeProfile && (
          <motion.div
            className="reveal"
            data-testid="profile-reveal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            onClick={(e) => { if (e.target === e.currentTarget) setActiveProfile(null); }}
          >
            <motion.div
              className="reveal-inner"
              initial={{ scale: 1.04, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 1.02, opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <img src={activeProfile.img} alt="" className="reveal-img" />
              <div className="reveal-grad" />

              {/* Gate overlay — "Not yet" state */}
              <AnimatePresence>
                {gateProfile && gateProfile.name === activeProfile.name && (
                  <motion.div
                    className="reveal-gate"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                  >
                    {gateStep === 1 && (
                      <motion.p
                        className="reveal-gate-line"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                      >
                        Not yet.
                      </motion.p>
                    )}
                    {gateStep === 2 && (
                      <>
                        <motion.p
                          className="reveal-gate-line reveal-gate-line--faint"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.4 }}
                        >
                          Not yet.
                        </motion.p>
                        <motion.p
                          className="reveal-gate-line"
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.15, duration: 0.5 }}
                        >
                          Start with a signal.
                        </motion.p>
                        <motion.button
                          className="reveal-gate-kiss"
                          onClick={(e) => { e.stopPropagation(); handleGateKiss(); }}
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.4, duration: 0.5 }}
                        >
                          Kiss
                        </motion.button>
                      </>
                    )}
                    {gateStep === 3 && (
                      <motion.div
                        className="reveal-gate-dots"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                      >
                        <span /><span /><span />
                      </motion.div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              <button
                className="reveal-close"
                onClick={() => setActiveProfile(null)}
                data-testid="reveal-close"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M15 19l-7-7 7-7"/></svg>
              </button>

              <div className="reveal-content">
                <motion.h2
                  className="reveal-name"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15, duration: 0.6 }}
                >
                  {activeProfile.name}, <span className="reveal-age">{activeProfile.age}</span>
                </motion.h2>
                <motion.p
                  className="reveal-status"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25, duration: 0.5 }}
                >
                  {activeProfile.status} &middot; {activeProfile.activity}
                </motion.p>
                {activeProfile.verified && (
                  <motion.span
                    className="reveal-badge"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.35, duration: 0.5 }}
                  >
                    Verified
                  </motion.span>
                )}

                <motion.div
                  className="reveal-actions"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.45, duration: 0.5 }}
                >
                  <button className="reveal-act reveal-act--message" type="button" onClick={(e) => { e.stopPropagation(); handleMessage(activeProfile); }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
                    <span>Message</span>
                  </button>
                  <button
                    className={`reveal-act reveal-act--kiss ${kissed.includes(activeProfile.name) ? 'reveal-act--kissed' : ''}`}
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleKiss(activeProfile.name); }}
                  >
                    <img src="/images/kiss-lips.png" alt="" className="reveal-act-lips" />
                    <span>{kissed.includes(activeProfile.name) ? 'Kissed' : 'Kiss'}</span>
                  </button>
                  <button className="reveal-act reveal-act--hotlist" type="button">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 2c.5 2.5 2 5 4 6.5S20 12 20 15a8 8 0 1 1-16 0c0-3 1.5-5 4-6.5S11.5 4.5 12 2z"/></svg>
                    <span>Hotlist</span>
                  </button>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Chat Overlay ── */}
      <AnimatePresence>
        {chatProfile && (
          <motion.div
            className="ncht"
            data-testid="chat-overlay"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="ncht-header">
              <button className="ncht-back" onClick={() => setChatProfile(null)} data-testid="chat-close">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M15 19l-7-7 7-7"/></svg>
              </button>
              <div className="ncht-avatar">
                <img src={chatProfile.img} alt="" />
              </div>
              <div className="ncht-meta">
                <span className="ncht-name">{chatProfile.name}</span>
                <span className="ncht-status">{chatProfile.activity}</span>
              </div>
            </div>

            <div className="ncht-thread">
              <div className="ncht-spacer" />
              <div className="ncht-map-msg">
                <div className="ncht-map">
                  <div className="ncht-map-pin" />
                  <span className="ncht-map-label">Approximate location</span>
                </div>
              </div>
              <motion.div
                className="ncht-bubble ncht-bubble--them"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
              >
                <span>Hey… I saw you.</span>
              </motion.div>
              <motion.div
                className="ncht-bubble ncht-bubble--you"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
              >
                <span>Did you?</span>
              </motion.div>
              <motion.div
                className="ncht-bubble ncht-bubble--them"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.5 }}
              >
                <span>Maybe.</span>
              </motion.div>
            </div>

            <div className="ncht-input-bar">
              <input className="ncht-input" type="text" placeholder="Say something..." readOnly />
              <button className="ncht-send" type="button">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M22 2L11 13"/><path d="M22 2L15 22 11 13 2 9z"/></svg>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AppPreviewPage;
